import React, { useEffect, useMemo, useState } from "react";
import { Rocket, Flag, CalendarClock, Pencil, FileDown, Loader2, ChevronDown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import { api, fmtDate, todayISO } from "../../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";

const DAY = 86400000;
const t = (s) => new Date(s + "T00:00:00").getTime();
const monday = (d) => {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
};
const daysUntil = (s) => Math.round((t(s) - t(todayISO())) / DAY);

export function useGoLive() {
  const { projects, tasks } = useApp();
  const [g, setG] = useState(null);
  const load = async () => setG((await api.get("/golive")).data);
  useEffect(() => {
    load();
  }, [projects, tasks]);
  return [g, setG, load];
}

export function WeeklyReportButton({ golive }) {
  const [busy, setBusy] = useState(false);
  const weeks = useMemo(() => {
    if (!golive?.programmeStart) return [];
    const start = Math.min(t(monday(golive.programmeStart)), t(monday(new Date())));
    const end = t(golive.goLiveDate || golive.programmeStart);
    const out = [];
    for (let x = start; x <= end + 6 * DAY && out.length < 80; x += 7 * DAY) out.push(new Date(x + 12 * 3600000).toISOString().slice(0, 10));
    return out;
  }, [golive]);
  const cur = monday(new Date());
  const [week, setWeek] = useState(null);
  const sel = week || (weeks.includes(cur) ? cur : weeks[0]);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/weekly", { params: { weekStart: sel }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Harrods_Weekly_Report_${sel}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Weekly report downloaded", { description: `Week commencing ${fmtDate(sel)}` });
    } catch {
      toast.error("Could not generate the report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="weekly-report">
      <Select value={sel || ""} onValueChange={setWeek}>
        <SelectTrigger className="h-9 w-52 bg-white text-xs" data-testid="report-week-select">
          <SelectValue placeholder="Select week" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {weeks.map((w) => (
            <SelectItem key={w} value={w}>
              w/c {fmtDate(w)}{w === cur ? " (this week)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        onClick={download}
        disabled={busy || !sel}
        data-testid="download-report-btn"
        className="flex h-9 items-center gap-1.5 rounded-lg border border-[#b8975a] bg-[#fbf8f1] px-3 text-sm font-semibold text-[#6e5a32] transition-colors hover:bg-[#f5efe3] disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Weekly Report (PDF)
      </button>
    </div>
  );
}

function EditDate({ g, setG }) {
  const [v, setV] = useState(g.goLiveDate || "");
  const [open, setOpen] = useState(false);
  const save = async (date) => {
    const r = await api.put("/golive", { date });
    setG(r.data);
    setOpen(false);
    toast.success(date ? "Go-live date updated" : "Go-live reset to programme end");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-[#d6b77a] hover:bg-white/10" data-testid="edit-golive-btn">
          <Pencil className="h-3 w-3" /> Edit date
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="start">
        <p className="mb-2 text-sm font-semibold text-slate-900">Go-live date</p>
        <Input type="date" value={v} onChange={(e) => setV(e.target.value)} data-testid="golive-date-input" />
        <div className="mt-3 flex gap-2">
          <button onClick={() => save(v)} disabled={!v} className="h-8 flex-1 rounded-md bg-[#00573f] text-xs font-semibold text-white hover:bg-[#004330]" data-testid="golive-save">Save</button>
          {g.overridden && (
            <button onClick={() => save(null)} className="h-8 flex-1 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50">Reset</button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-slate-400">Default: programme end ({fmtDate(g.computedDate)})</p>
      </PopoverContent>
    </Popover>
  );
}

export default function GoLiveCountdown({ g, setG }) {
  const { openTask } = useApp();
  const [showAll, setShowAll] = useState(false);
  if (!g || !g.goLiveDate) return null;
  const start = t(g.programmeStart), end = t(g.goLiveDate);
  const span = Math.max(end - start, DAY);
  const pos = (s) => Math.min(100, Math.max(0, ((t(s) - start) / span) * 100));
  const todayPos = pos(todayISO());
  const upcoming = g.milestones.filter((m) => !m.done && m.date >= todayISO());
  const list = showAll ? g.milestones : upcoming.slice(0, 4);
  const days = g.daysRemaining;
  const weeks = Math.floor(days / 7);

  return (
    <div className="login-bg relative mb-6 overflow-hidden rounded-2xl p-6 text-white shadow-lg" data-testid="golive-card">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#b8975a]/10 blur-2xl" />
      <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#d6b77a]">
            <Rocket className="h-4 w-4" /> Go-live countdown
          </p>
          <p className="mt-3 text-6xl font-extrabold leading-none tracking-tight" data-testid="golive-days">{days >= 0 ? days : 0}</p>
          <p className="mt-1 text-sm text-[#c9d6ce]">{days >= 0 ? `days to go · ${weeks} weeks` : "Go-live reached"}</p>
          <div className="mt-3 flex flex-wrap items-center gap-1">
            <span className="rounded-md bg-white/10 px-2 py-1 text-xs font-semibold">{fmtDate(g.goLiveDate)}</span>
            <EditDate g={g} setG={setG} />
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center justify-between text-xs text-[#9fb3a7]">
            <span>Start {fmtDate(g.programmeStart)}</span>
            <span>Overall progress <b className="text-white">{g.overallProgress}%</b></span>
            <span>Go-live {fmtDate(g.goLiveDate)}</span>
          </div>
          <TooltipProvider delayDuration={100}>
            <div className="relative mt-3 h-10">
              <div className="absolute left-0 right-0 top-[18px] h-1.5 rounded-full bg-white/10" />
              <div className="absolute left-0 top-[18px] h-1.5 rounded-full bg-[#b8975a]/70" style={{ width: `${todayPos}%` }} />
              <div className="absolute left-0 top-[18px] h-1.5 rounded-full bg-emerald-400" style={{ width: `${g.overallProgress}%` }} />
              {g.milestones.map((m) => (
                <Tooltip key={`${m.type}-${m.id}`}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => m.type === "milestone" && openTask(m.id)}
                      className={`absolute top-[13px] h-4 w-4 -translate-x-1/2 rounded-full border-2 transition-transform hover:scale-125 ${
                        m.done ? "border-emerald-300 bg-emerald-400" : m.type === "milestone" ? "border-[#d6b77a] bg-[#0c2a20]" : "border-white/60 bg-white/20"
                      } ${m.type === "milestone" ? "rotate-45 rounded-[3px]" : ""}`}
                      style={{ left: `${pos(m.date)}%` }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs font-semibold">{m.title}</p>
                    <p className="text-[11px] text-slate-500">{fmtDate(m.date)} · {m.type === "milestone" ? "Milestone" : "Project end"}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
              <div className="absolute top-0 h-10 w-0.5 -translate-x-1/2 bg-[#d6b77a]" style={{ left: `${todayPos}%` }}>
                <span className="absolute -top-0.5 left-1.5 whitespace-nowrap text-[10px] font-bold text-[#d6b77a]">Today</span>
              </div>
              <Flag className="absolute right-0 top-0 h-4 w-4 translate-x-1/2 text-[#d6b77a]" />
            </div>
          </TooltipProvider>
          <div className="mt-1 flex gap-4 text-[10px] text-[#9fb3a7]">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rotate-45 rounded-[2px] border-2 border-[#d6b77a]" /> Milestone</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border-2 border-white/60" /> Project end</span>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-3 rounded bg-emerald-400" /> Progress</span>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="golive-milestones">
            {list.map((m) => {
              const d = daysUntil(m.date);
              return (
                <button
                  key={`${m.type}-${m.id}`}
                  onClick={() => (m.type === "milestone" ? openTask(m.id) : null)}
                  className="rounded-xl border border-white/10 bg-white/5 p-3 text-left transition-colors hover:border-[#b8975a]/60 hover:bg-white/10"
                >
                  <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#d6b77a]">
                    {m.done ? <CheckCircle2 className="h-3 w-3 text-emerald-300" /> : m.type === "milestone" ? <Flag className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />}
                    {m.type === "milestone" ? "Milestone" : "Project end"}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm font-semibold leading-snug">{m.title}</p>
                  <p className="mt-1 text-xs text-[#9fb3a7]">{fmtDate(m.date)} · {m.done ? "done" : d >= 0 ? `in ${d}d` : `${-d}d ago`}</p>
                </button>
              );
            })}
          </div>
          <button onClick={() => setShowAll((s) => !s)} className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#d6b77a] hover:underline" data-testid="toggle-milestones">
            {showAll ? "Show next milestones" : `View all ${g.milestones.length} milestones`} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
    </div>
  );
}
